import { Reports } from "@app/shared/src/api/api.gen";
import {
    AggregatedTargetAttribute,
    AggregationPeriod,
    ITrafficReportConfiguration,
    TotalVolumeByPeriod,
    TrafficReportConfiguration,
} from "@app/shared/src/models";
import { useLoad } from "./useLoad";

/**
 * Vehicle volumes per day across the given devices, from the same report endpoint the main
 * dashboard charts with. Each bucket carries a count and the average velocity (m/s), which is
 * what backs both the traffic series and the fleet's average-speed KPI.
 */
export function useTraffic(deviceIds: string[], days: number) {
    return useLoad<TotalVolumeByPeriod[] | undefined>(async () => {
        if (deviceIds.length === 0) return [];
        const end = new Date();
        const start = new Date(end.getTime() - days * 86_400_000);
        return await Reports.getTotalVolume(
            AggregatedTargetAttribute.None,
            AggregationPeriod.Day,
            new TrafficReportConfiguration({
                inclusiveStart: start,
                exclusiveEnd: end,
                grouping: AggregatedTargetAttribute.None,
                deviceIds,
                groupingValues: [],
            } as unknown as ITrafficReportConfiguration),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deviceIds.join(","), days]);
}

/** Sum + count-weighted average speed over a set of volume buckets. */
export function summariseVolume(rows: TotalVolumeByPeriod[] | undefined): { total: number; avgMps: number | undefined } {
    if (!rows || rows.length === 0) return { total: 0, avgMps: undefined };
    let total = 0;
    let weighted = 0;
    for (const r of rows) {
        total += r.count ?? 0;
        weighted += (r.avgVelocity ?? 0) * (r.count ?? 0);
    }
    return { total, avgMps: total > 0 ? weighted / total : undefined };
}
